import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import wooCustomApiService from "@/api/services/wooCustomApiService";
import { handleApiError } from "@/helpers/apiErrorHelper";
import useAppDispatch from "@/hooks/useAppDispatch";
import type { CvsTypeName, PaymentMethodId, ShippingMethodId } from "@/constants/enums";
import { shippingMethodToCvsNameTypeMap } from "@/constants/mapping";
import { CheckoutReturnAction, type CVSStore } from "@/types/checkout";
import type { CvsShippingMethodId } from "@/types/shipping";

const { VITE_HEADLESS_SITE_DOMAIN } = import.meta.env;

//#region Types
export interface CvsRedirectPayload {
  shippingMethodId: ShippingMethodId;
  paymentMethodId: PaymentMethodId;
}

export interface CvsSelectionReturn {
  action: string | null;
  status: string | null;
  cvsStoreId: string | null;
  cvsStoreName: string | null;
  cvsStoreAddress: string | null;
  orderId: string | null;
  shippingMethodId: string | null;
  errorCode: string | null;
}

export interface UseCVSStoreReturn {
  cvsStore: CVSStore | null;
  cvsSelectionReturn: CvsSelectionReturn | null;
  cvsMapFormWrapRef: React.RefObject<HTMLDivElement | null>;
  isCVSStoreLoading: boolean;
  setCVSStore: React.Dispatch<React.SetStateAction<CVSStore | null>>;
  queryCVSMap: () => Promise<void>;
  getCvsTypeName: (shippingMethodId: CvsShippingMethodId) => CvsTypeName;
  resetCVSStore: () => void;
  handleCVSSelectionError: (errorCode: string | null) => string;
}
//#endregion

const useCVSStore = ({
  cvsRedirectPayload,
}: {
  cvsRedirectPayload: CvsRedirectPayload;
}): UseCVSStoreReturn => {
  const [searchParams] = useSearchParams();

  const appDispatch = useAppDispatch();

  const [cvsStore, setCVSStore] = useState<CVSStore | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const cvsMapFormWrapRef = useRef<HTMLDivElement | null>(null);

  const cvsSelectionReturn = useMemo(() => {
    if (searchParams.get("action") !== CheckoutReturnAction.CVS_STORE_SELECTION) return null;

    return {
      action: searchParams.get("action"),
      status: searchParams.get("status"),
      cvsStoreId: searchParams.get("cvs_store_id"),
      cvsStoreName: searchParams.get("cvs_store_name"),
      cvsStoreAddress: searchParams.get("cvs_store_address"),
      orderId: searchParams.get("order_id"),
      shippingMethodId: searchParams.get("shipping_method_id"),
      errorCode: searchParams.get("error_code"),
    };
  }, [searchParams]);

  const getCvsTypeName = (shippingMethodId: CvsShippingMethodId) => {
    return shippingMethodToCvsNameTypeMap[shippingMethodId] ?? "undefined";
  };

  const queryCVSMap = async () => {
    setIsLoading(true);
    try {
      const { data: formHTML } = await wooCustomApiService.ecpay.shipping.cvsMap({
        client_back_url: `${VITE_HEADLESS_SITE_DOMAIN}/checkout`,
        // 傳遞當前已選擇的資料，以便電子地圖跳轉回來後還原使用者的選擇
        shipping_method_id: cvsRedirectPayload.shippingMethodId,
        payment_method_id: cvsRedirectPayload.paymentMethodId,
      });

      if (cvsMapFormWrapRef.current) {
        cvsMapFormWrapRef.current.innerHTML = formHTML;
        (document.getElementById("ecpay-form") as HTMLFormElement).submit();
      }
    } catch (error) {
      handleApiError(error, appDispatch, "無法取得超商地圖，請稍後再試一次");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCVSSelectionError = (errorCode: string | null): string => {
    switch (errorCode) {
      case "cvs_store_selection_invalid":
        return "所選商店與所選寄送方式(離島/本島)不符";
      case "cvs_store_selection_failed":
      default:
        return "門市選擇結果發生錯誤，請重整頁面重新操作一次。";
    }
  };

  const resetCVSStore = () => {
    setCVSStore(null);
  };

  return {
    cvsStore,
    cvsMapFormWrapRef,
    cvsSelectionReturn,
    isCVSStoreLoading: isLoading,
    setCVSStore,
    queryCVSMap,
    handleCVSSelectionError,
    getCvsTypeName,
    resetCVSStore,
  };
};

export default useCVSStore;
